/**
 * PER-JURISDICTION entity RECORD selectability.
 *
 * This module answers exactly one question:
 *
 *     Is there a record to open for this entity type in THIS jurisdiction?
 *
 * LLC Globe is an internal review tool. Its job while the corporation migration
 * is in flight is to let a reviewer open every record that has been authored,
 * read it, compare it against the actual product, and send back corrections —
 * BEFORE more states are written against the same mistakes. A migrated record
 * that exists but cannot be opened is not reviewable, which makes it worse than
 * useless: it is work nobody can check.
 *
 * So the rule is record existence, and only record existence:
 *
 *     corpSelectable = a corporation record exists for this jurisdiction
 *
 * WHAT IS DELIBERATELY NOT IN THE RULE, and why each was removed:
 *
 *   product availability   `selectable_at_intake` / `not_offered` is a PRODUCT
 *                          fact — whether we sell corporation formation there
 *                          today. It is real, it is still carried in the data
 *                          and still projected to the CSR, and it is exactly the
 *                          kind of thing a reviewer needs to READ. Gating the
 *                          button on it hid twelve authored records from the
 *                          person whose job is to check them.
 *   global completeness    whether all 51 are migrated says nothing about
 *                          whether THIS record can be opened. It lives in
 *                          `readiness.ts` and is not an input here.
 *   global QA approval     likewise. QA cannot approve what it cannot open.
 *
 * Three layers, three files, three questions:
 *
 *   readiness.ts       is the whole 51-jurisdiction migration finished?
 *                      Engineering and rollout status.
 *   selectability.ts   is there a record to open here? What the selector reads.
 *   the record itself  is the product live here, what does it cost, what does
 *                      the state require? Content a reviewer reads after opening.
 *
 * Consequently a jurisdiction becomes selectable the moment its record is
 * authored — whatever that record says about launch status. No React change, no
 * list of state names, and no redeploy of UI logic is involved.
 */

import type { EntityType } from "./model";
import { ENTITY_TYPES } from "./model";

/**
 * The default entity type.
 *
 * LLC is the baseline: all 51 LLC records exist, and the state panel can render
 * LLC content from the legacy dataset even when the CSR bundle fails to load. It
 * is therefore the one entity type that is never gated, and the one anything
 * invalid falls back to.
 */
export const DEFAULT_ENTITY_TYPE: EntityType = "llc";

/**
 * The LLC / Corporation selector is permanent UI.
 *
 * Exported as a constant rather than a function of anything, so that nothing can
 * accidentally re-gate VISIBILITY: there is no argument to pass. Every entity
 * type renders on every one of the 51 jurisdiction cards. Selectability decides
 * only whether an option is ENABLED.
 */
export const ENTITY_SELECTOR_IS_PERMANENT = true as const;

/**
 * What a reviewer is told about a Corporation option that cannot be opened here.
 *
 * Deliberately free of migration and engineering vocabulary: no record counts,
 * no QA or readiness terminology. It says only that the information is not there
 * yet, which is now literally true of every disabled state — there is no record.
 */
export const CORPORATION_UNAVAILABLE_TITLE = "Corporation information not available yet";

/**
 * The only thing this module needs of a jurisdiction view: that it EXISTS.
 *
 * Structurally `object`, and that is the point rather than laziness. The rule
 * reads no field, so there is no field to get wrong and no field whose meaning
 * can drift into the gate later. A `CsrJurisdictionView` satisfies it, and so
 * does an empty object in a unit test — because in both cases a record is there.
 *
 * Kept structural rather than importing `CsrJurisdictionView` so that
 * `canonical/csr.ts` can keep importing this layer without a cycle.
 */
export type EntityRecordView = object;

/** The views a single jurisdiction has, by entity type. A missing key means no record. */
export type JurisdictionEntityViews = Partial<
  Record<EntityType, EntityRecordView | undefined>
>;

/**
 * Does a record exist for this entity type in this jurisdiction?
 *
 * A jurisdiction gets a view only when a record was authored for it, so view
 * presence IS record existence — there is no second source to keep in sync and
 * nothing to look up.
 *
 * Note what this does NOT consult: the record's own `productAvailability`. A
 * record marked `not_offered` is still a record, and is still reviewable.
 */
export function isEntityRecordAvailableForJurisdiction(
  view: EntityRecordView | undefined
): boolean {
  return view !== undefined && view !== null;
}

/**
 * Entity types that can actually be opened for this jurisdiction.
 *
 * This is the ENABLED set, not the rendered set: every type in `ENTITY_TYPES` is
 * always rendered, and the ones missing from this list render disabled.
 *
 * Global migration completeness is deliberately not an input. There is no
 * readiness parameter to pass, which is what makes it impossible to re-couple
 * the two by accident.
 */
export function selectableEntityTypesForJurisdiction(
  views: JurisdictionEntityViews | undefined
): readonly EntityType[] {
  return ENTITY_TYPES.filter(
    (entityType) =>
      entityType === DEFAULT_ENTITY_TYPE ||
      isEntityRecordAvailableForJurisdiction(views?.[entityType])
  );
}

/**
 * Coerce a possibly-stale selection back to something openable HERE.
 *
 * The selection follows the reviewer from state to state, so it has to survive a
 * jurisdiction change — and now it usually does, because moving between two
 * migrated states keeps Corporation active, which is what makes reviewing
 * fourteen records in a row bearable. What it must never survive is a move to a
 * jurisdiction with no record, where there is nothing to render.
 *
 * It takes the allowed list rather than any gate object, so every caller must
 * have computed selectability for the CURRENT jurisdiction first. There is no
 * way to pass it a global answer.
 */
export function resolveSelectedEntityType(
  requested: EntityType | undefined,
  allowedHere: readonly EntityType[]
): EntityType {
  if (requested && allowedHere.includes(requested)) return requested;
  return DEFAULT_ENTITY_TYPE;
}
